import {
  DoughBatchIngredient,
  CreateDoughBatchIngredientRequest,
  UpdateDoughBatchIngredientRequest
} from '../types/doughBatchIngredient';
import { DoughProductName } from '../types/doughBatch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const doughBatchIngredientApiService = {
  /**
   * Fetch all dough batch ingredients from the backend
   */
  fetchAllDoughBatchIngredients: async (): Promise<DoughBatchIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batch-ingredients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch dough batch ingredients');
      }

      return data.doughBatchIngredients;
    } catch (error) {
      console.error('Error fetching dough batch ingredients from API:', error);
      throw error;
    }
  },

  /**
   * Fetch dough batch ingredients by dough batch ID
   */
  fetchDoughBatchIngredientsByDoughBatchId: async (doughBatchId: string): Promise<DoughBatchIngredient[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batch-ingredients/by-dough-batch/${doughBatchId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch dough batch ingredients');
      }

      return data.doughBatchIngredients;
    } catch (error) {
      console.error('Error fetching dough batch ingredients by dough batch ID from API:', error);
      throw error;
    }
  },

  /**
   * Get default ingredients for a product type
   */
  getDefaultIngredients: async (productName: DoughProductName): Promise<string[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batch-ingredients/default-ingredients/${encodeURIComponent(productName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to get default ingredients');
      }

      return data.ingredients;
    } catch (error) {
      console.error('Error getting default ingredients from API:', error);
      throw error;
    }
  },

  /**
   * Create a new dough batch ingredient
   */
  createDoughBatchIngredient: async (ingredient: CreateDoughBatchIngredientRequest): Promise<DoughBatchIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batch-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ingredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create dough batch ingredient');
      }

      return data.doughBatchIngredient;
    } catch (error) {
      console.error('Error creating dough batch ingredient:', error);
      throw error;
    }
  },

  /**
   * Update an existing dough batch ingredient
   */
  updateDoughBatchIngredient: async (id: string, ingredient: UpdateDoughBatchIngredientRequest): Promise<DoughBatchIngredient> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batch-ingredients/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ingredient)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update dough batch ingredient');
      }

      return data.doughBatchIngredient;
    } catch (error) {
      console.error('Error updating dough batch ingredient:', error);
      throw error;
    }
  },

  /**
   * Delete a dough batch ingredient by ID
   */
  deleteDoughBatchIngredient: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/dough-batch-ingredients/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete dough batch ingredient');
      }
    } catch (error) {
      console.error('Error deleting dough batch ingredient:', error);
      throw error;
    }
  }
};
